/**
 * Real MCP Portal data source — agw-config-api admin endpoints.
 *
 * These are the concrete endpoints, discovered from the agw-config-api source
 * (cloudflare/agw/agw-config-api). The `/admin/*` surface is READ-ONLY and
 * gated by Cloudflare Access, so an engineer's own browser session
 * authenticates it — no service token, no Worker.
 *
 * Host (custom domains, config-api worker):
 *   prod:    https://config-production.agw.ai.cfdata.org
 *   staging: https://config-staging.agw.ai.cfdata.org
 *   dev:     https://config-dev.agw.ai.cfdata.org
 *   OpenAPI: https://config-production.agw.ai.cfdata.org/openapi.json
 *
 * Endpoints (all read-only, Cloudflare Access):
 *   GET /admin/:account_tag/:account_id/servers        → list servers
 *   GET /admin/:account_tag/:account_id/servers/:id     → read one server
 *   GET /admin/:account_tag/:account_id/portals         → list portals (+ servers[])
 *   GET /admin/:account_tag/:account_id/portals/:id     → read one portal
 *
 * Auth: Cloudflare Access injects `Cf-Access-Jwt-Assertion` at the edge from
 * the browser's session cookie. Send `credentials: 'include'` so the cookie
 * rides along. (Cross-origin from localhost needs the config-api to allow the
 * dev origin via CORS, or a dev proxy — see README.)
 *
 * Response envelope (Cloudflare API convention, via chanfana):
 *   { success: true, result: T | T[], result_info?: { total_count, ... } }
 *   { success: false, errors: [{ code, message }] }
 */

import type {
  PortalAccount,
  PortalServer,
  ConnectionStatus,
} from '../../../shared/types'
import type { PortalConnectionRecord } from './data'

/** One account the dashboard should pull. Team fills these in (their customers). */
export interface AccountRef {
  account_tag: string
  account_id: string
  /** Human-friendly label for the row; falls back to the tag. */
  account_name?: string
}

/** SHIP-14800 structured diagnostics attached to a failing server. */
export interface ErrorDetails {
  status_code?: number
  mcp_code?: number
  retryable?: boolean
  /** true = the MCP server returned an error; false = we couldn't reach it. */
  is_upstream?: boolean
  cause?: string
}

/** A server row as agw-config-api serializes it (fields we consume). */
export interface ApiServer {
  id: string
  name: string
  description?: string | null
  hostname?: string | null
  auth_type?: 'oauth' | 'bearer' | 'unauthenticated'
  /** "waiting" | "ready" | "stale" | "error" (free string, these are the observed values). */
  status?: string | null
  error?: string
  error_details?: ErrorDetails
  last_synced?: string | null
  last_successful_sync?: string | null
}

interface Envelope<T> {
  success: boolean
  result?: T
  errors?: Array<{ code?: number; message?: string }>
}

export interface PortalApiConfig {
  /** e.g. "https://config-production.agw.ai.cfdata.org" (no trailing slash). */
  baseUrl: string
  /** Accounts to pull; each becomes a row (customer) in the matrix. */
  accounts: AccountRef[]
}

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, {
    headers: { accept: 'application/json' },
    credentials: 'include', // send the Cloudflare Access session cookie
  })
  if (!res.ok) {
    throw new Error(`${url} → ${res.status} ${res.statusText}`)
  }
  const body = (await res.json()) as Envelope<T>
  if (!body.success || body.result === undefined) {
    const msg = body.errors?.map((e) => e.message).join('; ') || 'request failed'
    throw new Error(`${url} → ${msg}`)
  }
  return body.result
}

/** GET /admin/:tag/:id/servers — all servers configured on one account. */
export async function listServers(
  cfg: PortalApiConfig,
  acct: AccountRef
): Promise<ApiServer[]> {
  const url = `${cfg.baseUrl}/admin/${encodeURIComponent(acct.account_tag)}/${encodeURIComponent(acct.account_id)}/servers`
  return getJson<ApiServer[]>(url)
}

// ────────────────────────────────────────────────────────────────────────
// Mapping: agw-config-api server rows → dashboard types.
// ────────────────────────────────────────────────────────────────────────

/** Map the config-api `status` string onto the dashboard's ConnectionStatus. */
export function mapConnectionStatus(status: string | null | undefined): ConnectionStatus {
  switch (status) {
    case 'ready':
      return 'active'
    case 'error':
      return 'error'
    case 'stale':
      return 'inactive'
    case 'waiting':
    default:
      return 'unknown'
  }
}

/** Derive a display provider from the server hostname (e.g. api.slack.com → slack). */
function providerFromHostname(hostname?: string | null): string {
  if (!hostname) return 'Unknown'
  try {
    const host = new URL(hostname).hostname
    const parts = host.split('.').filter((p) => p !== 'www' && p !== 'api')
    const core = parts.length >= 2 ? parts[parts.length - 2] : host
    return core.charAt(0).toUpperCase() + core.slice(1)
  } catch {
    return hostname
  }
}

export function toPortalServer(s: ApiServer): PortalServer {
  return {
    server_id: s.id,
    server_name: s.name,
    provider: providerFromHostname(s.hostname),
    base_url: s.hostname ?? undefined,
    description: s.description ?? undefined,
  }
}

export function toAccount(acct: AccountRef): PortalAccount {
  return {
    account_id: acct.account_id,
    account_name: acct.account_name || acct.account_tag,
    is_active: true,
  }
}

export function toConnection(
  accountId: string,
  s: ApiServer
): PortalConnectionRecord {
  const status = mapConnectionStatus(s.status)
  // The config-api has no per-customer "blocked" flag (that comes from the
  // Prometheus mcp_customer_blocked metric in phase 2). Treat a non-retryable
  // upstream error as the strongest signal available today.
  const is_blocked =
    s.status === 'error' && s.error_details?.retryable === false
  return {
    account_id: accountId,
    server_id: s.id,
    connection_status: status,
    is_blocked,
    issue_count: s.status === 'error' ? 1 : 0,
    last_seen: s.last_successful_sync ?? s.last_synced ?? null,
  }
}

// ────────────────────────────────────────────────────────────────────────
// Aggregate across all configured accounts → the three data.ts primitives.
// Server columns are the union of every account's servers (dedup by id).
// ────────────────────────────────────────────────────────────────────────

async function fetchAll(cfg: PortalApiConfig): Promise<
  Array<{ acct: AccountRef; servers: ApiServer[] }>
> {
  return Promise.all(
    cfg.accounts.map(async (acct) => ({ acct, servers: await listServers(cfg, acct) }))
  )
}

export async function getAllServersLive(cfg: PortalApiConfig): Promise<PortalServer[]> {
  const all = await fetchAll(cfg)
  const byId = new Map<string, PortalServer>()
  for (const { servers } of all) {
    for (const s of servers) if (!byId.has(s.id)) byId.set(s.id, toPortalServer(s))
  }
  return [...byId.values()]
}

export async function getAllAccountsLive(cfg: PortalApiConfig): Promise<PortalAccount[]> {
  return cfg.accounts.map(toAccount)
}

export async function getAllConnectionsLive(
  cfg: PortalApiConfig
): Promise<PortalConnectionRecord[]> {
  const all = await fetchAll(cfg)
  const rows: PortalConnectionRecord[] = []
  for (const { acct, servers } of all) {
    for (const s of servers) rows.push(toConnection(acct.account_id, s))
  }
  return rows
}
